import { AwsRum, type AwsRumConfig } from "aws-rum-web";
import { version } from "../../../package.json";
import type { AppConfig } from "config/config";

type RumSettings = {
  appMonitorId: string;
  identityPoolId: string;
  guestRoleArn: string;
  region: string;
  sessionSampleRate: number;
};

type RumSessionAttributes = Record<string, string>;

const DEFAULT_SESSION_SAMPLE_RATE = 1;

const RUM_INCLUDED_URL_PATTERNS: RegExp[] = [/^[^?#]*$/];

const RUM_EXCLUDED_URL_PATTERNS: RegExp[] = [
  /cognito-identity\.([^.]*\.)?amazonaws\.com/,
  /sts\.([^.]*\.)?amazonaws\.com/,
];

const REPLAY_BLOCK_SELECTOR =
  '#signature-canvas, img[src^="data:"], audio, video, input[type="file"], [title], [download], input[type="checkbox"], input[type="radio"], input[type="hidden"]';

let rumClient: AwsRum | null = null;

function readRumValue(value: string | undefined): string | undefined {
  if (value === undefined || value === "" || value === "null") {
    return undefined;
  }

  return value;
}

function parseSessionSampleRate(value: string | undefined): number | null {
  const configuredRate = readRumValue(value);
  if (configuredRate === undefined) {
    return DEFAULT_SESSION_SAMPLE_RATE;
  }

  const rate = Number(configuredRate);
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : null;
}

function readRumSettings(config: AppConfig): RumSettings | null {
  const appMonitorId = readRumValue(config.rumAppMonitorId);
  const identityPoolId = readRumValue(config.rumIdentityPoolId);
  const guestRoleArn = readRumValue(config.rumGuestRoleArn);
  const region = readRumValue(config.rumRegion);
  const sessionSampleRate = parseSessionSampleRate(config.rumSessionSampleRate);

  if (
    appMonitorId === undefined ||
    identityPoolId === undefined ||
    guestRoleArn === undefined ||
    region === undefined ||
    sessionSampleRate === null
  ) {
    return null;
  }

  return {
    appMonitorId,
    identityPoolId,
    guestRoleArn,
    region,
    sessionSampleRate,
  };
}

function buildRumConfig(settings: RumSettings, userId: string): AwsRumConfig {
  return {
    identityPoolId: settings.identityPoolId,
    guestRoleArn: settings.guestRoleArn,
    userId,
    allowCookies: false,
    sessionSampleRate: settings.sessionSampleRate,
    sessionEventLimit: 0,
    recordResourceUrl: false,
    telemetries: [
      "errors",
      "performance",
      [
        "http",
        {
          urlsToInclude: RUM_INCLUDED_URL_PATTERNS,
          urlsToExclude: RUM_EXCLUDED_URL_PATTERNS,
        },
      ],
      [
        "replay",
        {
          recordOptions: {
            blockSelector: REPLAY_BLOCK_SELECTOR,
          },
        },
      ],
    ],
  };
}

export function initRum(config: AppConfig, userId: string): void {
  if (rumClient !== null) {
    return;
  }

  const settings = readRumSettings(config);
  if (settings === null) {
    return;
  }

  try {
    rumClient = new AwsRum(
      settings.appMonitorId,
      version,
      settings.region,
      buildRumConfig(settings, userId)
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

export function recordRumError(error: Error): void {
  rumClient?.recordError(error);
}

export function addRumSessionAttributes(
  attributes: RumSessionAttributes
): void {
  rumClient?.addSessionAttributes(attributes);
}
